import { PoolSizeAdjustmentInput, PoolSizeAdjustmentReason } from 'shared';
import { create } from 'zustand';

type AdjustPoolSizeState = {
  /** Opened from the Edit Pool link: the flow starts at the reason step, which Back returns to. */
  canGoBack: boolean;
  close: () => void;
  /** Back from a path to the reason step, which keeps that path's reason selected. */
  goBack: () => void;
  isOpen: boolean;
  /**
   * Opened from Edit Pool: takes the confirmed adjustment, which the blade applies with its own
   * save and drops on cancel. Null submits the adjustment straight away.
   */
  onStage: ((input: PoolSizeAdjustmentInput) => void) | null;
  /** Without a `reason` the modal asks why the pool size changes before showing a path. */
  open: (payload: {
    onStage?: (input: PoolSizeAdjustmentInput) => void;
    poolId: string;
    reason?: PoolSizeAdjustmentReason;
    stagedPoolSize?: number;
  }) => void;
  poolId: null | string;
  /** Reason of the path Back left, preselected on the reason step; null on a fresh opening. */
  previousReason: null | PoolSizeAdjustmentReason;
  /** Current path; null while the reason step is shown. */
  reason: null | PoolSizeAdjustmentReason;
  /** Forgets the pool and path, for when the page hosting the modal goes away. */
  reset: () => void;
  selectReason: (reason: PoolSizeAdjustmentReason) => void;
  /**
   * Opened from Edit Pool with adjustments already staged: the size they leave the pool at, which
   * this adjustment starts from. Null starts from the pool's saved size.
   */
  stagedPoolSize: null | number;
};

const INITIAL_STATE = {
  canGoBack: false,
  isOpen: false,
  onStage: null,
  poolId: null,
  previousReason: null,
  reason: null,
  stagedPoolSize: null,
};

export const adjustPoolSizeStore = create<AdjustPoolSizeState>()((set) => ({
  ...INITIAL_STATE,
  open: ({ onStage, poolId, reason, stagedPoolSize }) =>
    set({
      canGoBack: !reason,
      isOpen: true,
      onStage: onStage ?? null,
      poolId,
      previousReason: null,
      reason: reason ?? null,
      stagedPoolSize: stagedPoolSize ?? null,
    }),
  selectReason: (reason) => set({ reason }),
  goBack: () => set(({ reason }) => ({ previousReason: reason, reason: null })),
  // Keeps the pool and path: the modal still shows them while it fades out, and the next `open`
  // replaces every field anyway.
  close: () => set({ isOpen: false }),
  reset: () => set(INITIAL_STATE),
}));
